import React, { useEffect, useState } from 'react';
import {
  TableDensity,
  TablePreferenceColumn,
  TablePreferences,
} from '../hooks/useTablePreferences';

type Props<TKey extends string> = {
  columns: readonly TablePreferenceColumn<TKey>[];
  preferences: TablePreferences<TKey>;
  onWidthChange: (key: TKey, value: number) => void;
  onVisibilityChange: (key: TKey, visible: boolean) => void;
  onDensityChange: (density: TableDensity) => void;
  onFontSizeChange?: (fontSize: number) => void;
  onReset: () => void;
};

export default function TablePreferencesPanel<TKey extends string>({
  columns,
  preferences,
  onWidthChange,
  onVisibilityChange,
  onDensityChange,
  onFontSizeChange,
  onReset,
}: Props<TKey>) {
  const [widthDrafts, setWidthDrafts] = useState<Record<TKey, string>>(() => {
    const drafts = {} as Record<TKey, string>;
    columns.forEach(column => { drafts[column.key] = String(preferences.widths[column.key]); });
    return drafts;
  });

  useEffect(() => {
    const drafts = {} as Record<TKey, string>;
    columns.forEach(column => { drafts[column.key] = String(preferences.widths[column.key]); });
    setWidthDrafts(drafts);
  }, [columns, preferences.widths]);

  const commitWidth = (key: TKey) => {
    const raw = widthDrafts[key].replace(',', '.').trim();
    const value = Number(raw);
    if (raw && Number.isFinite(value)) {
      onWidthChange(key, value);
      return;
    }
    setWidthDrafts(previous => ({ ...previous, [key]: String(preferences.widths[key]) }));
  };

  return (
    <details className="crm-table-preferences">
      <summary>⚙ Налаштування таблиці</summary>
      <div className="crm-table-preferences__body">
        <label className="crm-table-preferences__density">
          Щільність рядків
          <select
            value={preferences.density}
            onChange={event => onDensityChange(event.target.value as TableDensity)}
          >
            <option value="comfortable">Звичайна</option>
            <option value="compact">Компактна</option>
          </select>
        </label>
        {onFontSizeChange && (
          <label className="crm-table-preferences__density">
            Розмір шрифту
            <select
              aria-label="Розмір шрифту таблиці"
              value={preferences.fontSize}
              onChange={event => onFontSizeChange(Number(event.target.value))}
            >
              {[11, 12, 13, 14, 15, 16].map(size => (
                <option key={size} value={size}>{size} px</option>
              ))}
            </select>
          </label>
        )}
        <div className="crm-table-preferences__columns">
          {columns.map(column => (
            <div className="crm-table-preferences__column" key={column.key}>
              <label className="crm-table-preferences__visibility">
                <input
                  type="checkbox"
                  checked={preferences.visibility[column.key]}
                  disabled={column.locked}
                  onChange={event => onVisibilityChange(column.key, event.target.checked)}
                />
                {column.label}
              </label>
              <label className="crm-table-preferences__width">
                <input
                  type="number"
                  min={column.minWidth}
                  max={column.maxWidth}
                  step="1"
                  inputMode="numeric"
                  aria-label={`Ширина колонки «${column.label}», відсотків`}
                  value={widthDrafts[column.key]}
                  onChange={event => setWidthDrafts(previous => ({ ...previous, [column.key]: event.target.value }))}
                  onBlur={() => commitWidth(column.key)}
                  onKeyDown={event => { if (event.key === 'Enter') event.currentTarget.blur(); }}
                />
                %
              </label>
            </div>
          ))}
        </div>
        <button type="button" className="crm-table-preferences__reset" onClick={onReset}>
          Скинути налаштування
        </button>
      </div>
    </details>
  );
}
